import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGlobalAction, useSession, useAction, useFindFirst } from "@gadgetinc/react";
import { api } from "../../../api";
import { useToast } from "@chakra-ui/react";
import { isSystemAdmin } from "./configHelpers";

const InstitutionConfigContext = createContext(null);

export function useInstitutionConfig() {
  const ctx = useContext(InstitutionConfigContext);
  if (!ctx) {
    throw new Error("useInstitutionConfig must be used within an InstitutionConfigProvider");
  }
  return ctx;
}

export function InstitutionConfigProvider({ children }) {
  const { institution } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const toast = useToast();

  // ── Global actions ──────────────────────────────────────────────
  const [{ data: configData, error: fetchError, fetching }, getConfig] = useGlobalAction(api.getInstitutionalConfig);
  const [{ data: updateData, error: updateError, fetching: updating }, updateConfig] = useGlobalAction(api.updateInstitutionalConfig);

  // ── Direct query for institution config (immediate toggle updates) ──
  const [{ data: institutionConfig, fetching: fetchingConfig, error: configQueryError }, refetchInstitutionConfig] = useFindFirst(
    api.institutionConfiguration,
    {
      filter: { institution: { equals: decodeURIComponent(institution) } },
      select: {
        id: true,
        institution: true,
        enableScenarioDebriefAudio: true,
        enableScenarioGenerator: true,
        enableEnhancedScenarios: true,
        institutionUnlimitedScenarios: true,
        defaultScenarioLimit: true,
        enableShiftScheduling: true,
        enableCalendarIntegration: true,
        enablePatientEncounterTracking: true,
        enablePreceptorEvaluations: true,
        enablePreceptorScoring: true,
        enableClinicalQuickNotes: true,
        enableFisdapShiftProcessing: true,
        requirePreceptorApproval: true,
        accessCodeSystemEnabled: true,
        cohortManagementEnabled: true,
        timezone: true,
        subscriptionTier: true,
        unlimitedScenarios: true,
        purchasedSeats: true,
        usedSeats: true,
        allowStudentScenarioAccess: true,
        // New forms & compliance fields
        enableFormsLibrary: true,
        enableVarianceDetection: true,
        enablePhiDetection: true,
        enableEncounterVerification: true,
        formQrCodeExpirationMinutes: true,
        formQrCodeSettings: true,
      }
    }
  );

  const [{ fetching: updatingConfig }, updateInstitutionConfig] = useAction(api.institutionConfiguration.update);

  // ── Local state ────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [reason, setReason] = useState("");
  const effectiveInstitutionConfig = institutionConfig || configData || config || null;

  // ── Watermark state & hooks ────────────────────────────────────
  const [watermarkFormData, setWatermarkFormData] = useState({
    watermarkingEnabled: true,
    overtWatermarkText: "\u00a9 {year} {institution} - Educational Use Only",
    overtWatermarkPosition: "diagonal",
    overtWatermarkOpacity: 0.15,
    overtWatermarkColor: "#FF0000",
    subtleWatermarkEnabled: true,
    pdfProtectionEnabled: true,
    pdfWarningText: { markdown: "This document is for educational purposes only. Unauthorized distribution is prohibited." }
  });

  const [{ data: watermarkConfig, fetching: fetchingWatermark, error: watermarkFetchError }, refetchWatermarkConfig] = useFindFirst(
    api.watermarkConfiguration,
    {
      filter: { institution: { equals: decodeURIComponent(institution) } },
      select: {
        id: true,
        institution: true,
        watermarkingEnabled: true,
        overtWatermarkText: true,
        overtWatermarkPosition: true,
        overtWatermarkOpacity: true,
        overtWatermarkColor: true,
        subtleWatermarkEnabled: true,
        subtleWatermarkPattern: true,
        pdfProtectionEnabled: true,
        pdfWarningText: { markdown: true },
        createdBy: { id: true }
      }
    }
  );

  const [{ fetching: creatingWatermark }, createWatermarkConfig] = useAction(api.watermarkConfiguration.create);
  const [{ fetching: updatingWatermark }, updateWatermarkConfig] = useAction(api.watermarkConfiguration.update);

  // ── Derived values ─────────────────────────────────────────────
  const decodedInstitution = decodeURIComponent(institution);
  const userIsSystemAdmin = isSystemAdmin(session);

  // ── Effects ────────────────────────────────────────────────────
  // Fetch configuration on mount
  useEffect(() => {
    if (institution && session?.user?.id) {
      getConfig({
        institution: decodedInstitution,
        userId: session.user.id
      });
    }
  }, [institution, session?.user?.id]);

  // Update local state when config data is fetched
  useEffect(() => {
    if (configData) {
      setConfig(configData);
      setFormData(configData);
    }
  }, [configData]);

  // Update watermark form data when watermark config is fetched
  useEffect(() => {
    if (watermarkConfig) {
      setWatermarkFormData({
        watermarkingEnabled: watermarkConfig.watermarkingEnabled ?? true,
        overtWatermarkText: watermarkConfig.overtWatermarkText || "\u00a9 {year} {institution} - Educational Use Only",
        overtWatermarkPosition: watermarkConfig.overtWatermarkPosition || "diagonal",
        overtWatermarkOpacity: watermarkConfig.overtWatermarkOpacity ?? 0.15,
        overtWatermarkColor: watermarkConfig.overtWatermarkColor || "#FF0000",
        subtleWatermarkEnabled: watermarkConfig.subtleWatermarkEnabled ?? true,
        subtleWatermarkPattern: watermarkConfig.subtleWatermarkPattern || "",
        pdfProtectionEnabled: watermarkConfig.pdfProtectionEnabled ?? true,
        pdfWarningText: watermarkConfig.pdfWarningText || { markdown: "This document is for educational purposes only. Unauthorized distribution is prohibited." }
      });
    }
  }, [watermarkConfig]);

  // Handle successful update
  useEffect(() => {
    if (updateData?.success) {
      toast({
        title: "Configuration Updated",
        description: "Institution configuration has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      if (institution && session?.user?.id) {
        getConfig({
          institution: decodedInstitution,
          userId: session.user.id
        });
      }
    }
  }, [updateData]);

  // ── Handlers ───────────────────────────────────────────────────
  const handleUpdateField = useCallback(async (field, value) => {
    const previousValue = effectiveInstitutionConfig?.[field];
    setFormData(prev => ({ ...prev, [field]: value }));

    try {
      await updateConfig({
        institution: decodedInstitution,
        configurationUpdates: { [field]: value },
        reason: `Updated ${field} via toggle`
      });
      await refetchInstitutionConfig();
      if (institution && session?.user?.id) {
        await getConfig({
          institution: decodedInstitution,
          userId: session.user.id
        });
      }
      toast({
        title: "Setting Updated",
        description: `${field} has been updated successfully.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating field:", error);
      toast({
        title: "Error",
        description: error.message || `Failed to update ${field}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setFormData(prev => ({
        ...prev,
        [field]: previousValue
      }));
    }
  }, [
    effectiveInstitutionConfig,
    decodedInstitution,
    updateConfig,
    refetchInstitutionConfig,
    toast,
    institution,
    session?.user?.id,
    getConfig
  ]);

  const handleNestedUpdate = useCallback((parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  }, []);

  const handleUpdateConfig = useCallback(async (updates) => {
    try {
      await updateConfig({
        institution: decodedInstitution,
        configurationUpdates: updates,
        reason: reason || "Configuration update"
      });
      toast({
        title: 'Configuration updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (institution && session?.user?.id) {
        getConfig({
          institution: decodedInstitution,
          userId: session.user.id
        });
      }
    } catch (error) {
      toast({
        title: 'Error updating configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [decodedInstitution, reason, updateConfig, institution, session?.user?.id, getConfig, toast]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const updates = {};

    if (formData.timezone !== config.timezone) {
      updates.timezone = formData.timezone;
    }

    Object.keys(formData).forEach(key => {
      const currentValue = formData[key];
      const originalValue = config[key];

      if (typeof currentValue === 'object' && currentValue !== null) {
        if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
          updates[key] = currentValue;
        }
      } else {
        if (currentValue !== originalValue) {
          updates[key] = currentValue;
        }
      }
    });

    if (Object.keys(updates).length === 0) {
      toast({
        title: "No Changes",
        description: "No configuration changes detected.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await updateConfig({
      institution: decodedInstitution,
      configurationUpdates: updates,
      reason: reason || "Configuration update"
    });
  }, [formData, config, decodedInstitution, reason, updateConfig, toast]);

  const handleWatermarkConfigSave = useCallback(async () => {
    try {
      if (!session?.user?.id) {
        toast({
          title: "Error",
          description: "User session not found. Please refresh and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (watermarkConfig?.id) {
        await updateWatermarkConfig({
          id: watermarkConfig.id,
          watermarkingEnabled: watermarkFormData.watermarkingEnabled,
          overtWatermarkText: watermarkFormData.overtWatermarkText,
          overtWatermarkPosition: watermarkFormData.overtWatermarkPosition,
          overtWatermarkColor: watermarkFormData.overtWatermarkColor
        });
      } else {
        await createWatermarkConfig({
          institution: decodedInstitution,
          watermarkingEnabled: watermarkFormData.watermarkingEnabled,
          overtWatermarkText: watermarkFormData.overtWatermarkText,
          overtWatermarkPosition: watermarkFormData.overtWatermarkPosition,
          overtWatermarkColor: watermarkFormData.overtWatermarkColor,
          overtWatermarkOpacity: watermarkFormData.overtWatermarkOpacity,
          subtleWatermarkEnabled: watermarkFormData.subtleWatermarkEnabled,
          pdfProtectionEnabled: watermarkFormData.pdfProtectionEnabled,
          subtleWatermarkPattern: watermarkFormData.subtleWatermarkPattern || `uuid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdBy: { _link: session.user.id }
        });
      }

      toast({
        title: "Watermark Configuration Saved",
        description: "Watermark settings have been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      refetchWatermarkConfig();
    } catch (error) {
      toast({
        title: "Error Saving Watermark Configuration",
        description: error.message || "Failed to save watermark settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [session, watermarkConfig, watermarkFormData, decodedInstitution, updateWatermarkConfig, createWatermarkConfig, refetchWatermarkConfig, toast]);

  const resetForm = useCallback(() => {
    setFormData(config);
    setReason("");
  }, [config]);

  const resetWatermarkForm = useCallback(() => {
    if (watermarkConfig) {
      setWatermarkFormData({
        watermarkingEnabled: watermarkConfig.watermarkingEnabled ?? true,
        overtWatermarkText: watermarkConfig.overtWatermarkText || "\u00a9 {year} {institution} - Educational Use Only",
        overtWatermarkPosition: watermarkConfig.overtWatermarkPosition || "diagonal",
        overtWatermarkOpacity: watermarkConfig.overtWatermarkOpacity ?? 0.15,
        overtWatermarkColor: watermarkConfig.overtWatermarkColor || "#FF0000",
        subtleWatermarkEnabled: watermarkConfig.subtleWatermarkEnabled ?? true,
        subtleWatermarkPattern: watermarkConfig.subtleWatermarkPattern || "",
        pdfProtectionEnabled: watermarkConfig.pdfProtectionEnabled ?? true,
        pdfWarningText: watermarkConfig.pdfWarningText || { markdown: "This document is for educational purposes only. Unauthorized distribution is prohibited." }
      });
    }
  }, [watermarkConfig]);

  // ── Context value ──────────────────────────────────────────────
  const value = {
    // URL params
    institution,
    decodedInstitution,

    // Navigation
    navigate,

    // Session
    session,
    userIsSystemAdmin,

    // Toast
    toast,

    // Config fetching
    config,
    configData,
    fetchError,
    fetching,
    getConfig,

    // Config updating
    updateData,
    updateError,
    updating,
    updateConfig,

    // Direct institution config query
    institutionConfig: effectiveInstitutionConfig,
    fetchingConfig,
    configQueryError,
    refetchInstitutionConfig,

    // Direct update action
    updatingConfig,
    updateInstitutionConfig,

    // Form state
    formData,
    setFormData,
    reason,
    setReason,

    // Watermark state
    watermarkFormData,
    setWatermarkFormData,
    watermarkConfig,
    fetchingWatermark,
    watermarkFetchError,
    refetchWatermarkConfig,
    creatingWatermark,
    updatingWatermark,

    // Handlers
    handleUpdateField,
    handleNestedUpdate,
    handleUpdateConfig,
    handleSubmit,
    handleWatermarkConfigSave,
    resetForm,
    resetWatermarkForm,
  };

  return (
    <InstitutionConfigContext.Provider value={value}>
      {children}
    </InstitutionConfigContext.Provider>
  );
}
